
import React, { useState } from 'react';
/* Added Activity to the lucide-react imports to resolve the 'Cannot find name Activity' error on line 156 */
import { Mail, Globe, MapPin, Send, AlertTriangle, CheckCircle2, Activity } from 'lucide-react';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    protocol: 'CYBER_SECURITY_V4',
    content: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  // Honeypot field for spam mitigation
  const [hp, setHp] = useState('');

  // Sanitize input to prevent XSS (OWASP A03)
  const sanitize = (str: string) => {
    return str.replace(/[<>]/g, '').trim();
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.name || formData.name.length < 2) newErrors.name = 'IDENT_FAILED: NAME_REQUIRED';
    if (!emailRegex.test(formData.email)) newErrors.email = 'CHANNEL_ERROR: INVALID_FORMAT';
    if (formData.content.length < 10) newErrors.content = 'DATA_VOID: CONTENT_INSUFFICIENT';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (hp) return; // Silent discard for bot submission
    
    if (!validate()) return;

    setStatus('sending');
    
    // Simulate secure API transmission with latency
    setTimeout(() => {
      setStatus('success');
      setFormData({ name: '', email: '', protocol: 'CYBER_SECURITY_V4', content: '' });
      setTimeout(() => setStatus('idle'), 5000);
    }, 2000);
  };

  return (
    <section id="contact" className="py-40 relative overflow-hidden scroll-mt-32">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="glass-premium rounded-[4rem] border-white/5 overflow-hidden grid lg:grid-cols-2 shadow-[0_50px_100px_rgba(0,0,0,0.8)]">
          
          <div className="p-16 lg:p-24 bg-brand-500/5 relative overflow-hidden">
            <div className="absolute -top-24 -left-24 w-64 h-64 bg-brand-500/10 blur-[100px] rounded-full" />
            <div className="hologram-line opacity-20" />
            
            <div className="relative z-10 space-y-12">
              <div className="space-y-6">
                <h2 className="text-[11px] font-black text-brand-400 font-mono tracking-[0.5em] uppercase">CONTACT PROTOCOL</h2>
                <h3 className="text-5xl font-black text-white leading-tight tracking-tighter">Initiate Secure <br /> Dialogue.</h3>
                <p className="text-slate-400 text-lg font-light leading-relaxed max-w-sm">
                  Connect with our systems architects to deploy elite-grade security for your digital infrastructure.
                </p>
              </div>
              
              <div className="space-y-10">
                <div className="flex items-center gap-8 group">
                  <div className="w-16 h-16 rounded-[1.5rem] bg-white/5 border border-white/10 flex items-center justify-center text-brand-400 group-hover:bg-brand-500 group-hover:text-white transition-all duration-500 shadow-xl group-hover:shadow-brand-500/20">
                    <Mail className="w-7 h-7" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-slate-500 font-black tracking-widest mb-1">SECURE ENCRYPTED MAIL</div>
                    <div className="text-xl font-black text-white tracking-tight">intel@sentrixastra.com</div>
                  </div>
                </div>
                <div className="flex items-center gap-8 group">
                  <div className="w-16 h-16 rounded-[1.5rem] bg-white/5 border border-white/10 flex items-center justify-center text-purple-400 group-hover:bg-purple-500 group-hover:text-white transition-all duration-500 shadow-xl group-hover:shadow-purple-500/20">
                    <Globe className="w-7 h-7" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-slate-500 font-black tracking-widest mb-1">DIGITAL DOMAIN</div>
                    <div className="text-xl font-black text-white tracking-tight">sentrixastra.com</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="p-16 lg:p-24 bg-black/40 backdrop-blur-3xl relative">
            <div className="hologram-line opacity-10" style={{ animationDelay: '2s' }} />
            
            <form className="space-y-8 relative z-10" onSubmit={handleSubmit}>
              {/* Honeypot field hidden from real users */}
              <input type="text" value={hp} onChange={e => setHp(e.target.value)} className="hidden" aria-hidden="true" />
              
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">IDENTIFICATION</label>
                  <input 
                    type="text" 
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: sanitize(e.target.value)})}
                    placeholder="SECURE_NAME" 
                    className={`w-full bg-white/5 border ${errors.name ? 'border-red-500/50' : 'border-white/10'} rounded-2xl px-6 py-4 text-sm font-mono focus:ring-1 focus:ring-brand-500 outline-none transition-all placeholder:text-slate-700 text-white`} 
                  />
                  {errors.name && <p className="text-[9px] font-mono text-red-400 mt-1 uppercase tracking-widest">{errors.name}</p>}
                </div>
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">CHANNEL_ID</label>
                  <input 
                    type="email" 
                    value={formData.email}
                    onChange={e => setFormData({...formData, email: sanitize(e.target.value)})}
                    placeholder="EMAIL_ENCRYPTED" 
                    className={`w-full bg-white/5 border ${errors.email ? 'border-red-500/50' : 'border-white/10'} rounded-2xl px-6 py-4 text-sm font-mono focus:ring-1 focus:ring-brand-500 outline-none transition-all placeholder:text-slate-700 text-white`} 
                  />
                  {errors.email && <p className="text-[9px] font-mono text-red-400 mt-1 uppercase tracking-widest">{errors.email}</p>}
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">PROTOCOL_TYPE</label>
                <select 
                  value={formData.protocol}
                  onChange={e => setFormData({...formData, protocol: e.target.value})}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-sm font-mono focus:ring-1 focus:ring-brand-500 outline-none transition-all text-slate-400 appearance-none"
                >
                  <option value="CYBER_SECURITY_V4">CYBER_SECURITY_V4</option>
                  <option value="AI_AUTOMATION_STREAMS">AI_AUTOMATION_STREAMS</option>
                  <option value="DIGITAL_ARCHITECTURES">DIGITAL_ARCHITECTURES</option>
                </select>
              </div>

              <div className="space-y-3">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">INSTRUCTION_SET</label>
                <textarea 
                  rows={4} 
                  value={formData.content}
                  onChange={e => setFormData({...formData, content: sanitize(e.target.value)})}
                  placeholder="TRANSMISSION_CONTENT..." 
                  className={`w-full bg-white/5 border ${errors.content ? 'border-red-500/50' : 'border-white/10'} rounded-2xl px-6 py-4 text-sm font-mono focus:ring-1 focus:ring-brand-500 outline-none transition-all resize-none placeholder:text-slate-700 text-white`}
                ></textarea>
                {errors.content && <p className="text-[9px] font-mono text-red-400 mt-1 uppercase tracking-widest">{errors.content}</p>}
              </div>

              <button 
                type="submit"
                disabled={status !== 'idle' && status !== 'error'}
                className={`w-full py-6 rounded-2xl ${status === 'success' ? 'bg-green-500' : 'bg-brand-500'} hover:opacity-90 font-black text-xs tracking-[0.4em] uppercase transition-all shadow-[0_20px_50px_rgba(14,140,233,0.3)] flex items-center justify-center gap-3 group active:scale-[0.98] overflow-hidden relative`}
              >
                <div className="hologram-line" />
                {status === 'idle' && <>EXECUTE TRANSMISSION <Send className="w-4 h-4" /></>}
                {status === 'sending' && <>PROTOCOL_SYNC_ACTIVE <Activity className="w-4 h-4 animate-spin" /></>}
                {status === 'success' && <>TRANSMISSION_COMPLETE <CheckCircle2 className="w-4 h-4" /></>}
                {status === 'error' && <>CRITICAL_FAILURE <AlertTriangle className="w-4 h-4" /></>}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
